import { useCallback, useMemo, useState } from 'react';

import { Group, SuggestGroupRequest } from '@cobu/api-client';
import { logger } from '../../utils/logger';
import {
  useOrganizedGroups,
  useJoinGroupMutation,
  useLeaveGroupMutation,
  useSuggestGroupMutation
} from './groupsHooks';

interface UseGroupsReturn {
  // Data
  myGroups: Group[];
  otherGroups: Group[];
  allGroups: Group[];

  // State
  isLoading: boolean;
  error: string | null;

  // Actions
  joinGroup: (groupId: string, role?: number) => Promise<void>;
  leaveGroup: (groupId: string) => Promise<void>;
  suggestGroup: (request: SuggestGroupRequest) => Promise<void>;
  clearErrors: () => void;
  refetch: () => Promise<void>;
  handleGroupsRefresh: () => Promise<void>;

  // Loading states for individual actions
  isJoining: boolean;
  isLeaving: boolean;
  isSuggesting: boolean;
  isRefreshing: boolean;
}

export function useGroups(): UseGroupsReturn {
  const { myGroups, otherGroups, allGroups, isLoading, error, clearErrors, refetch } = useOrganizedGroups();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const joinGroupMutation = useJoinGroupMutation();
  const leaveGroupMutation = useLeaveGroupMutation();
  const suggestGroupMutation = useSuggestGroupMutation();

  // Memoize the combined error to avoid unnecessary re-renders
  const combinedError = useMemo(() => {
    if (error) return error;
    if (joinGroupMutation.error) return joinGroupMutation.error.message;
    if (leaveGroupMutation.error) return leaveGroupMutation.error.message;
    if (suggestGroupMutation.error) return suggestGroupMutation.error.message;
    return null;
  }, [error, joinGroupMutation.error, leaveGroupMutation.error, suggestGroupMutation.error]);

  const handleJoinGroup = useCallback(
    async (groupId: string, role = 0) => {
      await joinGroupMutation.mutateAsync({ groupId, role });
    },
    [joinGroupMutation]
  );

  const handleLeaveGroup = useCallback(
    async (groupId: string) => {
      await leaveGroupMutation.mutateAsync(groupId);
    },
    [leaveGroupMutation]
  );

  const handleSuggestGroup = useCallback(
    async (request: SuggestGroupRequest) => {
      await suggestGroupMutation.mutateAsync(request);
    },
    [suggestGroupMutation]
  );

  const handleRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleGroupsRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (error) {
      logger.error('Error refreshing groups:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  return {
    // Data
    myGroups,
    otherGroups,
    allGroups,

    // State
    isLoading,
    error: combinedError,

    // Actions
    joinGroup: handleJoinGroup,
    leaveGroup: handleLeaveGroup,
    suggestGroup: handleSuggestGroup,
    clearErrors,
    refetch: handleRefetch, // Wrapper to match interface signature
    handleGroupsRefresh,

    // Loading states
    isJoining: joinGroupMutation.isPending,
    isLeaving: leaveGroupMutation.isPending,
    isSuggesting: suggestGroupMutation.isPending,
    isRefreshing,
  };
} 